/**
 * Batch Genealogy — Lineage Graph (React Flow DAG).
 *
 * Vertical lineage: final product on top, ancestry flowing DOWN into stage
 * bands (Step E → A), parent→product edges, status-colour-coded batch cards.
 * Each stage row is CENTRED on a common axis, so a parent card sits centred
 * above its children (a symmetric pyramid). Clicking a card raises
 * onSelectBatch(id) so the parent can open the batch detail side panel.
 *
 * The stage/product-description labels are NOT canvas nodes — they live in a
 * fixed rail pinned to the LEFT edge. Each label tracks its row's live screen
 * position from the React Flow viewport, so it zooms/pans vertically in sync
 * with its row (and scrolls out of frame when the row does) while staying
 * locked to the left corner horizontally.
 */
import React, { useMemo, useState } from 'react';
import ReactFlow, {
  Background, Controls, Handle, Position, MarkerType, ReactFlowProvider, useViewport,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { STATUS } from '../statusConfig';

const CARD_W = 250;
const ROW_H = 220;
const COL_W = 300;

// Left rail holding the stage name + product description. LABEL_W is the strip
// reserved on the left; the React Flow canvas starts just to its right so cards
// can never slide under the labels (and are clipped away when panned left).
//
// The label text is rendered at a FIXED, readable size (it is NOT scaled down
// with the canvas zoom any more) so the stage name + product description are
// legible without the user having to zoom in. To make sure a long chemical name
// can never spill into the stage below, each block is hard-capped to the live
// on-screen height of one row (ROW_H * zoom − ROW_GAP) and the overflow is
// clipped with an ellipsis; hovering the block pops the full description.
const LABEL_W = 280;
const ROW_GAP = 16;               // min visible gap between a label block and the next row
const STAGE_FONT = 16;            // stage name  (was 15, scaled with zoom → tiny)
const DESC_FONT = 13.5;           // product description
const LABEL_DESC_LINES = 10;      // safety line-clamp; the maxHeight is the real overlap guard

const BatchNode = ({ data }) => {
  const n = data.node;
  const s = STATUS[n.status] || STATUS.released;
  const consumedLine =
    n.parents && n.parents.length ? n.parents.join(' + ') : (n.keyMaterial || '— raw materials');
  return (
    <div
      style={{
        width: CARD_W, background: '#fff', border: '1px solid #e5e7eb',
        borderTop: `3px solid ${s.color}`, borderRadius: 12,
        padding: '14px 16px', boxShadow: '0 2px 6px rgba(15,23,42,0.10)', cursor: 'pointer',
      }}
    >
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 12, height: 12, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
        <span style={{ fontSize: 16, fontWeight: 700, color: '#000000' }}>{n.id}</span>
        {n.dev && (
          <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, color: '#fff', background: '#f59e0b', borderRadius: 10, padding: '2px 8px' }}>⚑ DEV</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 24, marginTop: 10 }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: '#000000', textTransform: 'uppercase' }}>Yield</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#333333', fontVariantNumeric: 'tabular-nums' }}>{n.yield}%</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: '#000000', textTransform: 'uppercase' }}>Qty</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#333333', fontVariantNumeric: 'tabular-nums' }}>{n.qty}</div>
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#4338ca', marginTop: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {consumedLine}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12.5 }}>
        <span style={{ color: '#333333' }}>{`${n.materials} materials`}</span>
        <span style={{ color: '#ea580c', fontWeight: 600 }}>All materials ›</span>
      </div>
    </div>
  );
};

const nodeTypes = { batch: BatchNode };

// Descending stage comparator (final on top) — mirrors the middleware's
// compareStageDesc so a step order can be synthesised on the client when needed.
const compareStageDesc = (a, b) => String(b || '').localeCompare(String(a || ''));

/**
 * Fixed left rail. Reads the live viewport (pan x/y + zoom) and re-positions
 * each label at its row's current screen-Y so the label stays glued to its row
 * vertically while pinned to the left corner.
 *
 * The text is rendered at a FIXED readable size (no zoom-scaling) so it is
 * legible without zooming in. Each block is capped to the live on-screen height
 * of one row so a long product description can never overlap the stage below;
 * the overflow is clipped with an ellipsis and hovering pops the full text
 * (onHover raises it to the parent, which renders the tooltip over the canvas so
 * it is not clipped by this rail's overflow:hidden). The container keeps
 * pointerEvents:none so panning/zooming works through the empty rail, but each
 * label block re-enables pointer events for the hover.
 */
const StepLabelRail = ({ rows, onHover }) => {
  const { y: vy, zoom } = useViewport();
  // Available on-screen height for one row; keep a floor so labels stay usable
  // even when the whole tree is zoomed far out.
  const blockMaxH = Math.max(56, ROW_H * zoom - ROW_GAP);
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, width: LABEL_W, height: '100%',
        overflow: 'hidden', pointerEvents: 'none', zIndex: 5,
      }}
    >
      {rows.map((r) => (
        <div
          key={r.id}
          onMouseEnter={() => onHover({ ...r, top: r.y * zoom + vy })}
          onMouseLeave={() => onHover(null)}
          style={{
            position: 'absolute', right: 0, top: r.y * zoom + vy, width: LABEL_W,
            textAlign: 'right', paddingRight: 20, boxSizing: 'border-box',
            pointerEvents: 'auto', cursor: 'default',
            // Hard-cap the block to one row's live on-screen height and clip the
            // overflow — the belt-and-braces guarantee a long chemical name can
            // NEVER grow down into the stage below, whatever the line count.
            maxHeight: blockMaxH, overflow: 'hidden',
          }}
        >
          <div style={{ fontSize: STAGE_FONT, fontWeight: 700, color: '#000000', marginBottom: 4, lineHeight: 1.25 }}>{r.label}</div>
          <div
            style={{
              fontSize: DESC_FONT, color: '#333333', lineHeight: 1.35,
              wordBreak: 'break-word',
              // Line-clamp with an ellipsis; the maxHeight on the parent is the
              // real overlap guard, this just tidies the truncation.
              display: '-webkit-box', WebkitLineClamp: LABEL_DESC_LINES, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {r.desc}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Full-text hover card for a stage rail label. Rendered by the parent (outside
 * the rail's overflow:hidden) so the whole product description is shown, never
 * clipped. Positioned just to the right of the rail, aligned to the hovered row.
 */
const StepLabelTooltip = ({ hover }) => {
  if (!hover) return null;
  return (
    <div
      style={{
        position: 'absolute', left: LABEL_W - 8, top: Math.max(6, hover.top),
        maxWidth: 380, background: '#111827', color: '#fff', borderRadius: 8,
        padding: '10px 12px', boxShadow: '0 6px 20px rgba(15,23,42,0.28)',
        zIndex: 20, pointerEvents: 'none',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{hover.label}</div>
      <div style={{ fontSize: 12.5, lineHeight: 1.4, color: '#e5e7eb', wordBreak: 'break-word' }}>{hover.desc}</div>
    </div>
  );
};

const LineageGraphInner = ({ steps, nodes, onSelectBatch }) => {
  const [hover, setHover] = useState(null);
  const { rfNodes, rfEdges, rows } = useMemo(() => {
    // Group the batch nodes by their stage code.
    const byStep = {};
    nodes.forEach((n) => { (byStep[n.step] = byStep[n.step] || []).push(n); });

    // Row order = the stages that ACTUALLY have nodes, in the order given by the
    // API `steps` list, with any extra node-stages appended (sorted final→first).
    // Deriving the order from the nodes themselves — rather than trusting the
    // steps list to match — guarantees every batch card is placed and rendered
    // even if steps[] and node.step ever drift apart.
    const stepMeta = new Map();
    (steps || []).forEach((s) => stepMeta.set(s.id, s));
    const fromSteps = (steps || []).map((s) => s.id).filter((id) => byStep[id]);
    const extra = Object.keys(byStep)
      .filter((id) => id && !fromSteps.includes(id))
      .sort(compareStageDesc);
    const order = [...fromSteps, ...extra];

    const rowOf = {};
    order.forEach((id, i) => { rowOf[id] = i; });

    // Centre each stage's cards on the common axis x = 0, so a single-batch
    // stage sits dead-centre and multi-batch stages spread symmetrically around
    // it — a parent card ends up centred above its children (the pyramid in the
    // design). fitView then centres the whole tree in the canvas.
    const cardNodes = [];
    order.forEach((stepId) => {
      const list = byStep[stepId] || [];
      const c = list.length;
      list.forEach((n, i) => {
        cardNodes.push({
          id: n.id,
          type: 'batch',
          position: { x: (i - (c - 1) / 2) * COL_W, y: rowOf[stepId] * ROW_H },
          data: { node: n },
          draggable: true,
        });
      });
    });

    // Row descriptors for the pinned left rail (graph-space Y per row + text).
    const rowList = order.map((id, i) => {
      const s = stepMeta.get(id) || { id, label: `Step ${id}`, desc: '' };
      return { id, y: rowOf[id] * ROW_H + 8, label: s.label, desc: s.desc };
    });

    const edges = [];
    nodes.forEach((n) => {
      (n.parents || []).forEach((pid) => {
        if (!nodes.find((x) => x.id === pid)) return;
        edges.push({
          id: `${pid}->${n.id}`,
          source: pid,
          target: n.id,
          // straight diagonal connectors — matches the design deck (not right-angle routing)
          type: 'straight',
          markerEnd: { type: MarkerType.ArrowClosed, color: '#b6becb', width: 16, height: 16 },
          style: { stroke: '#b6becb', strokeWidth: 1.5 },
        });
      });
    });

    return { rfNodes: cardNodes, rfEdges: edges, rows: rowList };
  }, [nodes, steps]);

  const handleNodeClick = (_evt, node) => {
    if (node.type === 'batch' && onSelectBatch) onSelectBatch(node.id);
  };

  return (
    <div style={{ position: 'relative', height: '100%', minHeight: 340, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fbfcfe', overflow: 'hidden' }}>
      {/* Canvas starts just right of the pinned label rail so cards never sit under a label. */}
      <div style={{ position: 'absolute', top: 0, left: LABEL_W, right: 0, bottom: 0 }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.3}
          maxZoom={1.5}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          // Don't let the graph hijack the mouse wheel. By default React Flow
          // preventDefaults wheel events to zoom/pan, which turns this fixed-height
          // canvas into a second scroll surface that fights the page scroll. With
          // these off, the wheel scrolls the PAGE straight through the graph (one
          // scroll for the whole report); zoom stays on the on-canvas Controls.
          zoomOnScroll={false}
          panOnScroll={false}
          preventScrolling={false}
          zoomOnDoubleClick={false}
        >
          <Background color="#e5e9f0" gap={22} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <StepLabelRail rows={rows} onHover={setHover} />
      <StepLabelTooltip hover={hover} />
    </div>
  );
};

const LineageGraph = (props) => (
  <ReactFlowProvider>
    <LineageGraphInner {...props} />
  </ReactFlowProvider>
);

export default LineageGraph;
